import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Globe2, ShieldAlert } from "lucide-react";
import { Badge, Button, Switch, cn } from "@fiskhub/ui";

/**
 * Cross-tenant mode (TODO.md item 3).
 *
 * `GET /v1/companies?all_tenants=true` and `GET /v1/devices?all_tenants=true`
 * return every tenant's rows to a session principal carrying
 * `users.is_platform_admin`. The capability is read-only and covers those two
 * resources only — there is no cross-tenant write path anywhere, and invoices,
 * certificates and users are never widened.
 *
 * The failure mode this module is designed against is not a leak — RLS and the
 * definer functions handle that — it is a FiskHub operator acting on another
 * customer's data believing it to be their own. So:
 *
 *  - the toggle exists ONLY for a user who actually holds the capability;
 *  - while it is on, a sticky banner sits above every screen and cannot be
 *    scrolled away, and the sidebar's tenant line is overwritten;
 *  - every row says which tenant it belongs to, and rows outside the operator's
 *    own tenant are marked as such in words, not only in colour;
 *  - the state is per browser tab (`sessionStorage`) and is dropped on sign-out
 *    with the rest of the tab's storage. It never persists across sessions,
 *    because a mode this consequential should not survive a closed laptop.
 */

const STORAGE_KEY = "fiskhub.cross-tenant";

interface CrossTenantValue {
  /** True only when the signed-in user holds `is_platform_admin`. */
  available: boolean;
  /** True when the toggle is on AND the capability is held. */
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** The operator's own tenant, for telling "mine" from "theirs" in a row. */
  ownTenantId: string;
  ownTenantName: string;
}

const CrossTenantContext = createContext<CrossTenantValue>({
  available: false,
  enabled: false,
  setEnabled: () => undefined,
  ownTenantId: "",
  ownTenantName: "",
});

function readStored(): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    // Private-mode storage failures must not take the dashboard down; the
    // safe default is the narrow view.
    return false;
  }
}

export function CrossTenantProvider({
  available,
  ownTenantId,
  ownTenantName,
  children,
}: {
  available: boolean;
  ownTenantId: string;
  ownTenantName: string;
  children: ReactNode;
}) {
  const [wanted, setWanted] = useState(readStored);

  // Losing the capability (a demotion, or a different user in the same tab)
  // must close the wide view rather than leave a stale banner on screen.
  useEffect(() => {
    if (!available && wanted) setWanted(false);
  }, [available, wanted]);

  const setEnabled = useCallback((next: boolean) => {
    setWanted(next);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      // Not being able to remember the choice is harmless; acting on it is not.
    }
  }, []);

  const value = useMemo<CrossTenantValue>(
    () => ({
      available,
      enabled: available && wanted,
      setEnabled,
      ownTenantId,
      ownTenantName,
    }),
    [available, wanted, setEnabled, ownTenantId, ownTenantName],
  );

  return <CrossTenantContext.Provider value={value}>{children}</CrossTenantContext.Provider>;
}

export function useCrossTenant(): CrossTenantValue {
  return useContext(CrossTenantContext);
}

/**
 * The persistent banner. Sticky, high-contrast, and impossible to scroll away
 * from — a checkbox somewhere above the fold is exactly what this must not be.
 */
export function CrossTenantBanner() {
  const { enabled, setEnabled, ownTenantName } = useCrossTenant();
  if (!enabled) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "sticky top-0 z-30 -mx-4 mb-2 flex flex-wrap items-center gap-3 border-b-4 px-4 py-3 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8",
        "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/20 backdrop-blur",
      )}
    >
      <ShieldAlert aria-hidden="true" className="size-6 shrink-0 text-[hsl(var(--warning))]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold uppercase tracking-wide">
          Cross-tenant view is ON — you are looking at every customer&rsquo;s data
        </p>
        <p className="text-xs text-muted-foreground">
          Companies and devices below are not limited to {ownTenantName}. Check the tenant of a row
          before acting on it. Every cross-tenant read is written to the audit log against your
          account.
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={() => setEnabled(false)}>
        Show only {ownTenantName}
      </Button>
    </div>
  );
}

/**
 * The toggle itself, placed on the two screens the capability actually covers.
 * Rendered as nothing at all for a user without the capability — a disabled
 * control would advertise a permission they do not have.
 */
export function CrossTenantToggle({ resource }: { resource: "companies" | "devices" }) {
  const { available, enabled, setEnabled, ownTenantName } = useCrossTenant();
  if (!available) return null;

  return (
    <Switch
      checked={enabled}
      onCheckedChange={setEnabled}
      className="sm:max-w-md"
      stateLabels={{ on: "All tenants", off: `${ownTenantName} only` }}
      label="Cross-tenant view"
      description={
        enabled
          ? `Showing ${resource} belonging to every tenant on the platform. Read-only, and audited against your account.`
          : `You are a platform administrator, so you may list ${resource} across every tenant. Off by default — turn it on deliberately.`
      }
    />
  );
}

/**
 * A table cell naming the tenant a row belongs to.
 *
 * The word "Other tenant" carries the meaning; the colour only reinforces it.
 * Rendered only while the cross-tenant view is on, because in a tenant-scoped
 * list every row has the same answer and the column is noise.
 */
export function TenantCell({
  tenantId,
  tenantName,
}: {
  tenantId: string;
  tenantName?: string | null;
}) {
  const { ownTenantId, ownTenantName } = useCrossTenant();
  const own = tenantId === ownTenantId;
  const name = own ? ownTenantName : (tenantName ?? tenantId);

  return (
    <span className="flex flex-col gap-1">
      <span className={cn("text-sm", own ? "text-muted-foreground" : "font-semibold")}>{name}</span>
      {own ? (
        <Badge tone="outline">
          <span aria-hidden="true">●</span>
          Your tenant
        </Badge>
      ) : (
        <Badge tone="warning" icon={<Globe2 aria-hidden="true" />}>
          Other tenant
        </Badge>
      )}
    </span>
  );
}

/** The stripe that marks a foreign row down the whole table. */
export function foreignRowClass(enabled: boolean, own: boolean): string | undefined {
  if (!enabled || own) return undefined;
  return "border-l-4 border-l-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5";
}
